/**
 * Azure Files names and paths (plan Task 7.2; the name rules are the Task 7.0 wire observations): share names,
 * per-protocol file / directory names, share-relative paths, ref normalisation (the ONE value approval and execution
 * share), parent / join helpers, the delete-job anchor (§3.8) and the share-SAS path kind (S7-10). Process-agnostic
 * (renderer and main): `TextEncoder` only.
 */
import type { DirectoryRef, FileRef, FileServiceRef, FileShareProtocol } from '../../types/azureStorage';

const SEGMENT_MAX_BYTES = 255;
const PATH_MAX_BYTES = 2048;
const byteLength = (value: string): number => new TextEncoder().encode(value).byteLength;
// Task 7.0: SMB refuses `: * ?` (400 InvalidResourceName) and treats `\` as a separator; Learn adds `" | < >` and controls.
const SMB_FORBIDDEN = /["\\/:|<>*?]/u;
const hasControl = (value: string, maxCode: number): boolean => [...value].some((char) => char.charCodeAt(0) <= maxCode);

export function shareNameProblem(name: string): string | undefined {
  if (name.length < 3 || name.length > 63) return 'A share name must contain 3 to 63 characters.';
  if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/u.test(name) || name.includes('--')) {
    return 'A share name uses lowercase letters, numbers and single hyphens, and starts and ends with a letter or number.';
  }
  return undefined;
}

/** The named-share list of an Entra connection (plan §3.2): valid names only, trimmed, deduplicated, order kept. */
export function normalizeNamedShares(names: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const raw of names ?? []) {
    const name = raw.trim();
    if (!shareNameProblem(name) && !out.includes(name)) out.push(name);
  }
  return out;
}

export function fileNameProblem(name: string, protocol: FileShareProtocol): string | undefined {
  if (!name || name === '.' || name === '..') return 'Enter a name other than "." or "..".';
  if (byteLength(name) > SEGMENT_MAX_BYTES) return `A file or folder name can be at most ${SEGMENT_MAX_BYTES} bytes long.`;
  if (protocol === 'smb' && (SMB_FORBIDDEN.test(name) || hasControl(name, 0x1f))) return 'Names on SMB shares cannot contain " \\ / : | < > * ? or control characters.';
  if (protocol === 'nfs' && (name.includes('/') || hasControl(name, 0))) return 'Names on NFS shares cannot contain / or NUL characters.';
  return undefined;
}

export const pathSegments = (path: string): string[] => path.split('/').filter((segment) => segment !== '');

/** A share-relative path: '' (the root) or segments joined by single '/', no leading / trailing '/', no '.' / '..'. */
export function sharePathProblem(path: string, protocol?: FileShareProtocol): string | undefined {
  if (path === '') return undefined;
  if (byteLength(path) > PATH_MAX_BYTES) return `A path can be at most ${PATH_MAX_BYTES.toLocaleString('en-US')} bytes long.`;
  for (const segment of path.split('/')) {
    if (segment === '' || segment === '.' || segment === '..') return 'A path cannot contain empty, "." or ".." segments or a leading or trailing "/".';
    const problem = protocol ? fileNameProblem(segment, protocol) : byteLength(segment) > SEGMENT_MAX_BYTES ? fileNameProblem(segment, 'nfs') : undefined;
    if (problem) return problem;
  }
  return undefined;
}

export type NormalizedFileRef<T extends FileServiceRef> = { ok: true; ref: T } | { ok: false; problem: string };

/** Trims slashes, collapses doubled ones, refuses `.` / `..` / empty paths of a file; keeps `snapshot` and the shape. */
export function normalizeFileServiceRef<T extends FileServiceRef>(ref: T): NormalizedFileRef<T> {
  if (!ref.share) return { ok: false, problem: 'A share name is required.' };
  if (ref.kind === 'share') return { ok: true, ref: { ...ref } };
  const path = pathSegments(ref.path).join('/');
  if (ref.kind === 'file' && path === '') return { ok: false, problem: 'A file path is required.' };
  const problem = sharePathProblem(path);
  if (problem) return { ok: false, problem };
  return { ok: true, ref: { ...ref, path } };
}

export const joinSharePath = (directory: string, name: string): string => (directory ? `${directory}/${name}` : name);

/** The directory holding `ref` (the share root's parent is none), keeping the snapshot identity. */
export function parentDirectoryRef(ref: DirectoryRef | FileRef): DirectoryRef | undefined {
  const segments = pathSegments(ref.path);
  if (segments.length === 0) return undefined;
  return { kind: 'directory', share: ref.share, path: segments.slice(0, -1).join('/'), ...(ref.snapshot ? { snapshot: ref.snapshot } : {}) };
}

/**
 * The delete job's destination (D18, §3.8): one ref → that ref; several → the deepest common directory of their paths,
 * compared by segments ('' = the share root). Delete refs carry no snapshot; all belong to one share.
 */
export function deleteAnchorRef(refs: ReadonlyArray<DirectoryRef | FileRef>): DirectoryRef | FileRef {
  if (refs.length === 0) throw new Error('deleteAnchorRef needs at least one ref.');
  const distinct = new Set(refs.map((ref) => `${ref.kind}\u0000${pathSegments(ref.path).join('/')}`));
  if (distinct.size === 1) return refs[0];
  let common = pathSegments(refs[0].path);
  for (const ref of refs.slice(1)) {
    const segments = pathSegments(ref.path);
    let index = 0;
    while (index < common.length && index < segments.length && common[index] === segments[index]) index += 1;
    common = common.slice(0, index);
  }
  // A file ref's own name is never a directory of the others: the anchor is at most the deepest shared DIRECTORY.
  const fileRefWholePath = refs.some((ref) => ref.kind === 'file' && pathSegments(ref.path).length === common.length);
  if (fileRefWholePath) common = common.slice(0, -1);
  return { kind: 'directory', share: refs[0].share, path: common.join('/') };
}

export type SasSharePathKind = 'share' | 'directory' | 'file' | 'ambiguous';

/**
 * What a share SAS URL names (S7-10): `sr=f` → a file; no path below the share → the share; a trailing '/' (or
 * `sr=d`) → a directory; any other path under `sr=s` → ambiguous (a share SAS covers every file and folder, so the
 * path alone cannot tell) — the caller probes or asks.
 */
export function sasPathKindFromUrl(url: URL): SasSharePathKind {
  const resource = url.searchParams.get('sr');
  if (resource === 'f') return 'file';
  const below = url.pathname.split('/').slice(2).join('/');
  if (below === '') return 'share';
  if (resource === 'd' || below.endsWith('/')) return 'directory';
  return 'ambiguous';
}
